import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, describeAuthFormError, formErrorClassName, secondaryTextClassName, useAuth, type AuthFormError, isInvitationContinuation } from '@monecolier/ui';
import { acceptParentInvitation } from '@monecolier/api-client';
import { invitationContinuation } from '../lib/invitationContinuation';

/**
 * AUT-18 — page cible du lien envoyé par email au parent d'un élève importé en
 * masse (`POST /schools/:id/students/:studentId/invite-parent`, voir
 * `lib/parentInvitationEmail.ts` côté apps/api). `token` porté par la query
 * string (`?token=...`) — même contrat EXACT qu'`AcceptInvitation.tsx` (AUT-08,
 * web-admin) et `AcceptGuardianInvitation.tsx` (PAR-04, cette app).
 *
 * AUT-33 : « Se connecter »/« Créer un compte » propagent le jeton
 * (`lib/invitationContinuation.ts`, mécanisme partagé AUT-32) — une fois
 * authentifié, le parent revient ICI, l'acceptation (donc le rattachement de
 * l'enfant) se fait automatiquement, puis accueil direct. Remplace l'ancien
 * parcours en deux étapes (retour manuel sur le lien email).
 *
 * ⚠️ C'est ce parcours — et LUI SEUL — qui crée le compte : l'invitation
 * elle-même n'en crée aucun, et le mot de passe est choisi par le parent via le
 * formulaire d'inscription normal (`/register`), jamais généré/transmis par
 * l'école. Aucune ligne `Membership` n'est créée, à aucun moment : ce compte est
 * un compte parent standard, sans accès au back-office de l'établissement.
 *
 * Comme PAR-04, accepter exige un PROFIL PARENT déjà complété (`400
 * parent_profile_required` sinon) — `web-parent/Register.tsx` collecte déjà le
 * téléphone requis (PAR-03), donc un compte fraîchement créé depuis cette page
 * en a toujours un ; le cas d'un compte ancien sans profil est affiché comme une
 * erreur standard (le bandeau de complétion existe déjà sur `Home.tsx`).
 */
export function AcceptParentInvitation() {
  const { accessToken, apiClient, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  // AUT-33 : retour depuis /register ou /login (jeton propagé, voir
  // lib/invitationContinuation.ts) — sur succès, accueil direct.
  const continueAfterAuth = isInvitationContinuation(useLocation().state);
  const authContinuation = token ? { kind: 'parent' as const, token } : null;

  const [status, setStatus] = useState<'idle' | 'accepting' | 'accepted' | 'error'>('idle');
  const [error, setError] = useState<AuthFormError | null>(null);

  // AUT-33 : un seul appel d'acceptation par jeton — en dev, StrictMode rejoue
  // cet effet avant que `status` ne passe à 'accepting' (même garde qu'AUT-32,
  // web-admin/AcceptInvitation.tsx : un 409 "déjà acceptée" revenu en dernier
  // afficherait une erreur malgré un rattachement réussi).
  const acceptStartedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !accessToken || !token || status !== 'idle') return;
    if (acceptStartedFor.current === token) return;
    acceptStartedFor.current = token;
    setStatus('accepting');
    acceptParentInvitation(apiClient, token, accessToken)
      .then(() => setStatus('accepted'))
      .catch((err) => {
        setError(describeAuthFormError(err));
        setStatus('error');
      });
  }, [isAuthenticated, accessToken, apiClient, token, status]);

  if (!token) {
    return (
      <Card title={t('acceptParentInvitation.title')}>
        <p role="alert" data-testid="accept-parent-invitation-missing-token" className={formErrorClassName('generic')}>
          {t('acceptParentInvitation.missingToken')}
        </p>
      </Card>
    );
  }

  if (!isAuthenticated) {
    return (
      <Card title={t('acceptParentInvitation.anonymousTitle')}>
        <p className={secondaryTextClassName} data-testid="accept-parent-invitation-anonymous">
          {t('acceptParentInvitation.anonymousBody')}
        </p>
        <div className="flex flex-wrap gap-2">
          <Link to={invitationContinuation.authPath('/login', authContinuation)}>
            <Button type="button">{t('acceptParentInvitation.login')}</Button>
          </Link>
          <Link to={invitationContinuation.authPath('/register', authContinuation)}>
            <Button type="button" variant="secondary">
              {t('acceptParentInvitation.createAccount')}
            </Button>
          </Link>
        </div>
      </Card>
    );
  }

  if (status === 'accepted') {
    if (continueAfterAuth) return <Navigate to="/" replace />;
    return (
      <Card title={t('acceptParentInvitation.acceptedTitle')}>
        <p data-testid="accept-parent-invitation-success">{t('acceptParentInvitation.acceptedBody')}</p>
        <Link to="/">
          <Button type="button">{t('acceptParentInvitation.goToHome')}</Button>
        </Link>
      </Card>
    );
  }

  if (status === 'error' && error) {
    return (
      <Card title={t('acceptParentInvitation.title')}>
        <p role="alert" data-testid="accept-parent-invitation-error" className={formErrorClassName(error.kind)}>
          {error.message}
        </p>
      </Card>
    );
  }

  return (
    <Card title={t('acceptParentInvitation.title')}>
      <p className={secondaryTextClassName} role="status">
        {t('acceptParentInvitation.processing')}
      </p>
    </Card>
  );
}
